import { useState } from 'react';
import clsx from 'clsx';

import { Button, Card, Input, Textarea, Badge, Tag, Icon } from '@lib/ui';
import { Drawer, AnimatedTick } from '@lib/ui';
import { ArchetypeCard, DecisionSelector, PersonaPreview } from '../components';
import type { WizardPersona, PersonaDecisions } from '../types';
import {
  defaultPersona,
  ARCHETYPES,
  TRAIT_SUGGESTIONS,
  DECISION_DIMENSIONS,
  defaultDecisions,
} from '../types';

type WizardStep = 'select' | 'customize' | 'differentiate' | 'finalize';

export type TemplateWizardProps = {
  isOpen: boolean;
  onClose: () => void;
  onComplete?: (persona: WizardPersona) => void;
};

export function TemplateWizard({ isOpen, onClose, onComplete }: TemplateWizardProps) {
  const [step, setStep] = useState<WizardStep>('select');
  const [selectedArchetypeId, setSelectedArchetypeId] = useState<string | null>(null);
  const [persona, setPersona] = useState<Partial<WizardPersona>>(defaultPersona);
  const [isCompleted, setIsCompleted] = useState(false);

  const selectedArchetype = ARCHETYPES.find((a) => a.id === selectedArchetypeId);

  const updatePersona = (updates: Partial<WizardPersona>) => {
    setPersona((prev) => ({ ...prev, ...updates }));
  };

  const handleSelectArchetype = (archetypeId: string) => {
    const archetype = ARCHETYPES.find((a) => a.id === archetypeId);
    if (archetype) {
      setSelectedArchetypeId(archetypeId);
      // Pre-fill persona with archetype values
      setPersona({
        ...defaultPersona,
        ...archetype.persona,
        archetype: archetypeId,
        name: archetype.name,
      });
    }
  };

  const handleComplete = () => {
    setIsCompleted(true);
    setTimeout(() => {
      onComplete?.(persona as WizardPersona);
      onClose();
    }, 1500);
  };

  const traits = persona.voice?.traits || [];
  const decisions = persona.decisions || defaultDecisions;

  const toggleTrait = (trait: string) => {
    const currentTraits = persona.voice?.traits || [];
    if (currentTraits.includes(trait)) {
      updatePersona({
        voice: {
          ...persona.voice,
          traits: currentTraits.filter((t) => t !== trait),
          description: persona.voice?.description || '',
          examplePhrases: persona.voice?.examplePhrases || [],
          avoidPhrases: persona.voice?.avoidPhrases || [],
        },
      });
    } else if (currentTraits.length < 5) {
      updatePersona({
        voice: {
          ...persona.voice,
          traits: [...currentTraits, trait],
          description: persona.voice?.description || '',
          examplePhrases: persona.voice?.examplePhrases || [],
          avoidPhrases: persona.voice?.avoidPhrases || [],
        },
      });
    }
  };

  const updateDecision = (key: keyof PersonaDecisions, value: string) => {
    updatePersona({
      decisions: {
        ...decisions,
        [key]: value,
      },
    });
  };

  const goToStep = (newStep: WizardStep) => {
    setStep(newStep);
  };

  // Key decisions to show in differentiate step
  const keyDecisions = DECISION_DIMENSIONS.filter((d) =>
    ['confidence', 'callToAction', 'formality'].includes(d.key)
  );

  const renderStepContent = () => {
    // Completion state
    if (isCompleted) {
      return (
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center">
            <AnimatedTick color="success" size="xl" className="mx-auto mb-6" />
            <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-2">
              Persona Created!
            </h2>
            <p className="text-gray-500 dark:text-dark-400">
              "{persona.name}" is ready to start creating content.
            </p>
          </div>
        </div>
      );
    }

    switch (step) {
      case 'select':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Choose an Archetype
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-dark-400">
                Start with a template and customize it to your needs
              </p>
            </div>

            <div className="grid grid-cols-1 gap-4">
              {ARCHETYPES.map((archetype) => (
                <ArchetypeCard
                  key={archetype.id}
                  archetype={archetype}
                  selected={selectedArchetypeId === archetype.id}
                  onClick={() => handleSelectArchetype(archetype.id)}
                  showPreview
                />
              ))}
            </div>
          </div>
        );

      case 'customize':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Customize Your Persona
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-dark-400">
                Based on: {selectedArchetype?.name}
              </p>
            </div>

            {/* Pre-filled fields */}
            <Input
              label="Persona Name"
              value={persona.name || ''}
              onChange={(e) => updatePersona({ name: e.target.value })}
            />

            <Textarea
              label="Tagline"
              value={persona.tagline || ''}
              onChange={(e) => updatePersona({ tagline: e.target.value })}
              rows={2}
            />

            {/* Traits */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
                Personality Traits ({traits.length}/5)
              </label>
              <div className="flex flex-wrap gap-1.5">
                {TRAIT_SUGGESTIONS.map((trait) => (
                  <Tag
                    key={trait}
                    size="sm"
                    variant={traits.includes(trait) ? 'filled' : 'outlined'}
                    color={traits.includes(trait) ? 'primary' : 'neutral'}
                    className="cursor-pointer"
                    onClick={() => toggleTrait(trait)}
                  >
                    {trait}
                  </Tag>
                ))}
              </div>
            </div>

            <Textarea
              label="Voice Description"
              value={persona.voice?.description || ''}
              onChange={(e) =>
                updatePersona({
                  voice: {
                    ...persona.voice,
                    traits: persona.voice?.traits || [],
                    description: e.target.value,
                    examplePhrases: persona.voice?.examplePhrases || [],
                    avoidPhrases: persona.voice?.avoidPhrases || [],
                  },
                })
              }
              rows={3}
            />
          </div>
        );

      case 'differentiate':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                What Makes Yours Unique?
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-dark-400">
                Adjust key decisions to differentiate from the template
              </p>
            </div>

            {/* Show diff from template */}
            <Card skin="bordered" className="p-4 bg-gray-50 dark:bg-dark-700">
              <p className="text-xs font-medium text-gray-500 dark:text-dark-400 uppercase tracking-wider mb-2">
                Template: {selectedArchetype?.name}
              </p>
              <p className="text-sm text-gray-600 dark:text-dark-300">
                {selectedArchetype?.description}
              </p>
            </Card>

            {/* Key decision selectors */}
            <div className="space-y-8">
              {keyDecisions.map((dimension) => (
                <DecisionSelector
                  key={dimension.key}
                  dimension={dimension}
                  value={decisions[dimension.key]}
                  onChange={updateDecision}
                  compact
                />
              ))}
            </div>
          </div>
        );

      case 'finalize':
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Compare & Finalize
              </h2>
              <p className="mt-1 text-sm text-gray-500 dark:text-dark-400">
                See how your customized persona compares to the template
              </p>
            </div>

            {/* Side-by-side comparison */}
            <div className="space-y-4">
              <div>
                <Badge variant="soft" color="neutral" className="mb-2">
                  Template Output
                </Badge>
                <Card skin="bordered" className="p-4 bg-gray-50 dark:bg-dark-700">
                  <p className="text-sm text-gray-600 dark:text-dark-300 italic">
                    "{selectedArchetype?.exampleOutput}"
                  </p>
                </Card>
              </div>

              <div>
                <Badge variant="soft" color="primary" className="mb-2">
                  Your Customized Output
                </Badge>
                <PersonaPreview persona={persona} selectedChannel="LinkedIn" compact />
              </div>
            </div>

            {/* Summary */}
            <Card skin="bordered" className="p-4">
              <h3 className="font-medium text-gray-900 dark:text-white mb-3">{persona.name}</h3>
              <p className="text-sm text-gray-500 dark:text-dark-400 mb-3">{persona.tagline}</p>
              {traits.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {traits.map((trait) => (
                    <Badge key={trait} variant="soft" color="primary" className="text-xs">
                      {trait}
                    </Badge>
                  ))}
                </div>
              )}
            </Card>
          </div>
        );

      default:
        return null;
    }
  };

  const stepOrder: WizardStep[] = ['select', 'customize', 'differentiate', 'finalize'];
  const currentStepIndex = stepOrder.indexOf(step);

  const canProceed = () => {
    switch (step) {
      case 'select':
        return !!selectedArchetypeId;
      case 'customize':
        return !!persona.name?.trim();
      case 'differentiate':
        return true;
      case 'finalize':
        return true;
      default:
        return false;
    }
  };

  const footer = isCompleted ? null : (
    <>
      {currentStepIndex > 0 && (
        <Button variant="outlined" onClick={() => goToStep(stepOrder[currentStepIndex - 1])}>
          <Icon name="arrow-left" size="md" />
          <span className="ml-1">Back</span>
        </Button>
      )}
      <div className="flex-1" />
      {currentStepIndex < stepOrder.length - 1 ? (
        <Button onClick={() => goToStep(stepOrder[currentStepIndex + 1])} disabled={!canProceed()}>
          <span className="mr-1">Next</span>
          <Icon name="arrow-right" size="md" />
        </Button>
      ) : (
        <Button color="success" onClick={handleComplete}>
          <Icon name="check" size="md" />
          <span className="ml-1">Create Persona</span>
        </Button>
      )}
    </>
  );

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title="Create from Template"
      position="right"
      size="xl"
      footer={footer}
    >
      <div className="pb-4">
        {/* Progress indicator */}
        {!isCompleted && (
          <div className="flex items-center gap-2 mb-6">
            {stepOrder.map((s, idx) => (
              <div key={s} className="flex items-center">
                <div
                  className={clsx(
                    'size-2 rounded-full transition-colors',
                    idx <= currentStepIndex ? 'bg-primary-500' : 'bg-gray-300 dark:bg-dark-600'
                  )}
                />
                {idx < stepOrder.length - 1 && (
                  <div
                    className={clsx(
                      'w-8 h-0.5 mx-1 transition-colors',
                      idx < currentStepIndex ? 'bg-primary-500' : 'bg-gray-300 dark:bg-dark-600'
                    )}
                  />
                )}
              </div>
            ))}
            <span className="ml-2 text-xs text-gray-500 dark:text-dark-400">
              Step {currentStepIndex + 1} of {stepOrder.length}
            </span>
          </div>
        )}

        {renderStepContent()}
      </div>
    </Drawer>
  );
}

export default TemplateWizard;
